import { useAppSelector } from 'store/hooks';
import React from 'react';
import { Box } from '@mui/material';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';

import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { JourneyPageScenario } from 'components/order/model/Order';
import { parseFloatOrNull, parseIntOrNull, parseIntOrUndefined, stringOrNull } from 'lib/helpers';
import logger from 'lib/logger';
import { ILocationScopedSettingsComponentProps } from './LocationScopedSettings';
import { OrderingCard } from './Ordering/OrderingCard';
import { PayAtTableSettings } from './Ordering/PayAtTableSettings';
import { PreorderSettings } from './Ordering/PreorderSettings';
import { TimeslotsSettings } from './Ordering/TimeslotsSettings';
import { OrderToTableSettings } from './Ordering/OrderToTableSettings';
import { BartabSettings } from './Ordering/BartabSettings';
import { PayAtTableGlobalSettings } from './Ordering/PayAtTableGlobalSettings';
import { PayAtPOSSettings } from './Ordering/PayAtPOSSettings';
import { DeliveryProvider, DeliverySettings } from './DeliverySettings';

export const OrderingSettings: React.FC<ILocationScopedSettingsComponentProps> = ({
    settings,
    onSettingsSaved,
    locationId,
    globalSettings
}) => {
    const { tenantId } = useAppSelector(state => state.auth.tenant);

    const updateSettings = React.useCallback(
        async (
            commonSettings: IPublicAndPrivateSettings,
            locationSettings?: IPublicAndPrivateSettings,
            allSettings?: IPublicAndPrivateSettings
        ) => {
            try {
                let settingsToUpdate: IPublicAndPrivateSettings = allSettings ?? {};

                // Common Settings
                if (locationId === undefined) {
                    settingsToUpdate = {
                        ...settingsToUpdate,
                        ...commonSettings
                    };
                } else if (locationSettings) {
                    settingsToUpdate = { ...settingsToUpdate, ...locationSettings };
                }

                if (Object.keys(settingsToUpdate).length === 0) {
                    // No need to update anything
                    return true;
                }
                await new LocationSettingsApi().updatePublicAndPrivateSettings(settingsToUpdate, locationId);
                await onSettingsSaved();
                return true;
            } catch (err) {
                logger.error(`Error Updating Settings: ${JSON.stringify(err)}`);
                return false;
            }
        },
        [locationId, onSettingsSaved]
    );

    const handleSubmitOrdering = React.useCallback(
        async (values: Record<string, any>) => {
            const journeyPageScenarios = [];

            if (values.preorderEnabledJourney) {
                journeyPageScenarios.push(JourneyPageScenario.PREORDER);
            }
            if (values.ottEnabledJourney) {
                journeyPageScenarios.push(JourneyPageScenario.ORDER_TO_TABLE);
            }
            if (values.dineInEnabledJourney) {
                journeyPageScenarios.push(JourneyPageScenario.DINE_IN);
            }
            if (values.patEnabledJourney) {
                journeyPageScenarios.push(JourneyPageScenario.PAY_AT_TABLE);
            }

            return updateSettings(
                {
                    menu: {
                        calorieNutrientName: values.calorieNutrientName,
                        buyNowEnabled: values.buyNowEnabled,
                        useProductDetailsImageInMenuListing: values.useProductDetailsImageInMenuListing,
                        allergenUrl: values.allergenUrl
                    },
                    web: {
                        journeyPageScenarios,
                        captureNameOnOrder: {
                            DINE_IN: values.captureNameOnOrder_DINE_IN
                        }
                    },
                    app: {
                        showRecentOrders: values.showRecentOrders,
                        showCategoryGroups: values.showCategoryGroups
                    },
                    orderCardCheck: {
                        TABLE: values.cardCheckEnabledTable,
                        TAB: values.cardCheckEnabledTab,
                        ORDER_TO_TABLE: values.cardCheckEnabledOrderToTable,
                        PREORDER: values.cardCheckEnabledPreorder,
                        PAY_AT_POS: values.cardCheckEnabledPayAtPos
                    }
                },
                {
                    web: {
                        captureNameOnOrder: {
                            ORDER_TO_TABLE: values.captureNameOnOrder_ORDER_TO_TABLE,
                            DINE_IN: values.captureNameOnOrder_DINE_IN
                        }
                    },
                    menu: {
                        allergenUrl: values.allergenUrl
                    },
                    app: {
                        showCategoryGroups: values.showCategoryGroups
                    },
                    busyness: values.orderingBusyness,
                    orderingEnabled: values.orderingEnabled
                }
            );
        },
        [updateSettings]
    );

    const handleSubmitLocationPayAtTable = React.useCallback(
        async (values: Record<string, any>) =>
            updateSettings(
                {},
                {
                    payAtTable: {
                        retrievalBy: values.retrievalBy,
                        noTableCheckFoundUrl: values.noTableCheckFoundUrl
                    }
                }
            ),
        [updateSettings]
    );

    const handleSubmitPreorder = React.useCallback(
        async (values: Record<string, any>) =>
            updateSettings(
                {
                    preOrder: {
                        readyPeriodStart: parseIntOrUndefined(values.readyPeriodStart),
                        readyPeriodEnd: parseIntOrUndefined(values.readyPeriodEnd),
                        readyPeriodPadding: parseIntOrUndefined(values.readyPeriodPadding)
                    }
                },
                undefined,
                {
                    preOrderEnabled: values.preOrderEnabled,
                    asapOrderingEnabled: values.asapOrderingEnabled,
                    capturePhoneNumberOnOrder: {
                        PREORDER: values.capturePhoneNumberOnOrder_PREORDER
                    },
                    web: {
                        captureNameOnOrder: {
                            PREORDER: values.captureNameOnOrder_PREORDER
                        }
                    }
                }
            ),
        [updateSettings]
    );

    const handleSubmitOrderToTable = React.useCallback(
        async (values: Record<string, any>) =>
            updateSettings({
                orderToTableEnabled: values.orderToTableEnabled,
                orderToTable: {
                    limitLocationRange: values.limitLocationRange,
                    nfcEnabled: values.nfcEnabled,
                    multiPartEnabled: values.multiPartEnabled,
                    canChangeTableNumber: values.canChangeTableNumber
                },
                web: {
                    captureNameOnOrder: {
                        ORDER_TO_TABLE: values.captureNameOnOrder_ORDER_TO_TABLE
                    }
                }
            }),
        [updateSettings]
    );

    const handleSubmitGlobalPayAtTable = React.useCallback(
        async (values: Record<string, any>) =>
            updateSettings({
                payAtTableEnabled: values.payAtTableEnabled,
                payAtTable: {
                    nfcEnabled: values.nfcEnabled,
                    limitLocationRange: values.limitLocationRange,
                    canChangeTableNumber: values.canChangeTableNumber
                }
            }),
        [updateSettings]
    );

    const handleSubmitPayAtPos = React.useCallback(
        async (values: Record<string, any>) => updateSettings({ payAtPosEnabled: values.payAtPosEnabled }),
        [updateSettings]
    );

    const handleSubmitDelivery = React.useCallback(
        async (values: Record<string, any>) => {
            let settingsToUpdate: IPublicAndPrivateSettings = {
                capturePhoneNumberOnOrder: {
                    DELIVERY: values.capturePhoneNumberOnOrder_DELIVERY
                },
                orderPreparationTimeMins: {
                    DELIVERY: {
                        standard: parseIntOrNull(values.standardPrepTimeMins),
                        quiet: parseIntOrNull(values.quietPrepTimeMins),
                        busy: parseIntOrNull(values.busyPrepTimeMins)
                    }
                },
                uberDirect: {
                    customerId: stringOrNull(values.uberDirectCustomerId)
                }
            };
            if (locationId === undefined) {
                if (values.deliveryProvider === DeliveryProvider.UBER_DIRECT) {
                    settingsToUpdate = {
                        ...settingsToUpdate,
                        deliveryProvider: values.deliveryProvider,
                        uberDirect: {
                            customerId: values.uberDirectCustomerId,
                            clientId: values.uberDirectClientId,
                            clientSecret: values.uberDirectClientSecret,
                            webhookSecret: values.uberDirectWebhookSecret
                        }
                    };
                } else {
                    settingsToUpdate = {
                        ...settingsToUpdate,
                        deliveryProvider: values.deliveryProvider,
                        uberDirect: null
                    };
                }
                settingsToUpdate = {
                    ...settingsToUpdate,
                    delivery: {
                        activeOrderDurationPaddingMins: parseFloatOrNull(
                            values.activeOrderDurationPaddingMins
                        ),
                        baseFare: parseFloatOrNull(values.deliveryBaseFare),
                        baseDistance: parseFloatOrNull(values.deliveryBaseDistance),
                        costPerDistance: parseFloatOrNull(values.deliveryCostPerDistance),
                        distanceInFullIncrements: values.deliveryDistanceInFullIncrements,
                        distanceIncrement: parseFloatOrNull(values.deliveryDistanceIncrement),
                        distanceUnit: values.deliveryDistanceUnit,
                        minimumBasketAmount: parseFloatOrNull(values.deliveryMinimumBasketAmount),
                        minimumBasketAmountFee: parseFloatOrNull(values.deliveryMinimumBasketAmountFee),
                        chargePercentage: parseFloatOrNull(values.deliveryChargePercentage),
                        maximumChargeValue: parseFloatOrNull(values.deliveryMaximumChargeValue)
                    }
                };
            }
            return updateSettings(settingsToUpdate, settingsToUpdate);
        },
        [updateSettings, locationId]
    );

    const handleSubmitTimeslots = React.useCallback(
        async (values: Record<string, any>) =>
            updateSettings(
                {},
                {},
                {
                    timeSlotsEnabled: values.timeslotsEnabled,
                    timeSlotsProvider: values.timeslotsProvider,
                    ...(values.timeslotsProvider === 'PEPPER'
                        ? {
                              timeSlots: {
                                  standard: {
                                      ordersPerTimeslot: parseIntOrUndefined(
                                          values.standardOrdersPerTimeslot
                                      ),
                                      timeslotFrequencyInMins: parseIntOrUndefined(
                                          values.standardFrequencyInMins
                                      ),
                                      timeslotLengthInMins: parseIntOrUndefined(values.standardLengthInMins),
                                      windowLengthInMins: parseIntOrUndefined(
                                          values.standardWindowLengthInMins
                                      ),
                                      windowOffsetInMins: parseIntOrUndefined(
                                          values.standardWindowOffsetInMins
                                      )
                                  },
                                  busy: {
                                      ordersPerTimeslot: parseIntOrUndefined(values.busyOrdersPerTimeslot),
                                      timeslotFrequencyInMins: parseIntOrUndefined(
                                          values.busyFrequencyInMins
                                      ),
                                      timeslotLengthInMins: parseIntOrUndefined(values.busyLengthInMins),
                                      windowLengthInMins: parseIntOrUndefined(values.busyWindowLengthInMins),
                                      windowOffsetInMins: parseIntOrUndefined(values.busyWindowOffsetInMins)
                                  },
                                  quiet: {
                                      ordersPerTimeslot: parseIntOrUndefined(values.quietOrdersPerTimeslot),
                                      timeslotFrequencyInMins: parseIntOrUndefined(
                                          values.quietFrequencyInMins
                                      ),
                                      timeslotLengthInMins: parseIntOrUndefined(values.quietLengthInMins),
                                      windowLengthInMins: parseIntOrUndefined(values.quietWindowLengthInMins),
                                      windowOffsetInMins: parseIntOrUndefined(values.quietWindowOffsetInMins)
                                  }
                              }
                          }
                        : {})
                }
            ),
        [updateSettings]
    );

    return (
        <Box minWidth="500px">
            {/* Cards for each settings block */}
            <OrderingCard
                settings={settings}
                tenantId={tenantId}
                locationId={locationId}
                handleSubmitOrdering={handleSubmitOrdering}
            />
            {locationId !== undefined && (
                <PayAtTableSettings
                    settings={settings}
                    tenantId={tenantId}
                    onSubmit={handleSubmitLocationPayAtTable}
                />
            )}
            <PreorderSettings
                settings={settings}
                tenantId={tenantId}
                onSubmit={handleSubmitPreorder}
                locationId={locationId}
            />
            {locationId !== undefined && (
                <TimeslotsSettings settings={settings} onSubmit={handleSubmitTimeslots} />
            )}
            {locationId === undefined && (
                <>
                    <OrderToTableSettings
                        settings={settings}
                        tenantId={tenantId}
                        onSubmit={handleSubmitOrderToTable}
                    />
                    <BartabSettings tenantId={tenantId} />
                    <PayAtTableGlobalSettings
                        settings={settings}
                        tenantId={tenantId}
                        onSubmit={handleSubmitGlobalPayAtTable}
                    />
                    <PayAtPOSSettings settings={settings} onSubmit={handleSubmitPayAtPos} />
                </>
            )}
            <DeliverySettings
                settings={settings}
                globalSettings={globalSettings}
                locationId={locationId}
                onSubmit={handleSubmitDelivery}
            />
        </Box>
    );
};
